import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ko';

dayjs.locale('ko');

/**
 * 날짜 이동 바 — 오늘의 브리핑 / 회원 현황 / 강사 현황이 함께 쓴다.
 *
 * 원장은 "어제는 뭐였지", "지난주엔 이 회원이 어땠지"를 계속 되짚는다.
 *
 * 과거 날짜에서도 지시를 보낼 수 있어야 한다.
 * 원장이 매일 아침 브리핑을 챙긴다는 보장이 없다. 이틀 밀렸다가 오늘 몰아서
 * 처리하는 게 오히려 흔한 운영이다. 과거를 잠그면 밀린 사람에게는 기능이 없는 셈이 된다.
 *
 * 대신 데이터가 그날 기준이라는 사실은 숨기지 않는다.
 *
 * 이동 수단을 셋 준다. 지난달까지 가려고 화살표를 서른 번 누르게 하면 안 된다.
 *   ① ‹ › — 하루씩
 *   ② 주간 칩 — 고른 날이 속한 주. 화살표를 누르면 칩도 따라 움직인다.
 *   ③ 달력 — 날짜를 누르면 열린다. 달 단위로 건너뛸 때 쓴다.
 */

/** 브리핑 보관 기간 */
export const MAX_PAST_DAYS = 90;

type Props = {
    value: Dayjs;
    onChange: (next: Dayjs) => void;
    /** 화면 이름 — "8월 3일 회원 현황"처럼 읽히게 */
    label: string;
    /**
     * 그 화면이 실제로 집계하는 단위.
     *
     * 강사 현황은 날짜를 고르지만 조회는 **달 단위**입니다. 그런데 이 줄이
     * 늘 "27일 전 강사 현황"이라고 말해서, 같은 달 안에서 날짜만 옮겨도
     * 27일 전 숫자를 보고 있는 것처럼 읽혔습니다. 실제로는 같은 숫자입니다.
     * 화면이 자기 단위로만 말하게 합니다.
     */
    unit?: 'day' | 'month';
};

const BORDER = '#E3EAF3';

const DateNavigator = ({ value, onChange, label, unit = 'day' }: Props) => {
    const today = dayjs().startOf('day');
    const cur = value.startOf('day');
    const diff = today.diff(cur, 'day');
    const isToday = diff === 0;
    /** 달 단위 화면에서는 '지금'인지도 달로 따진다 */
    const isCurrentPeriod = unit === 'month' ? cur.isSame(today, 'month') : isToday;
    const oldest = today.subtract(MAX_PAST_DAYS, 'day');

    const [open, setOpen] = useState(false);
    /** 제목 버튼 hover — 테두리·배경이 인라인이라 CSS 가상클래스로는 못 준다 (QA39) */
    const [hover, setHover] = useState(false);
    /** 달력에서 넘겨보는 달. 날짜 선택과 별개로 움직인다 */
    const [viewMonth, setViewMonth] = useState<Dayjs>(cur.startOf('month'));
    const wrapRef = useRef<HTMLDivElement>(null);

    // 달력을 열 때는 항상 지금 보고 있는 날짜의 달부터
    useEffect(() => {
        if (open) setViewMonth(cur.startOf('month'));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // 바깥을 누르면 닫힌다
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const selectable = (d: Dayjs) => !d.isAfter(today) && !d.isBefore(oldest);

    const go = (next: Dayjs) => {
        if (!selectable(next)) return;
        onChange(next.startOf('day'));
    };

    /*
     * ─────────────────────────────────────────────────────────────
     * 🔴 **달 단위 화면에서는 화살표도 달로 움직인다** (2026-09-01 QA28)
     * ─────────────────────────────────────────────────────────────
     * `unit="month"` 는 원래 **부제 한 줄과 색 판정만** 바꿨다. 제목은
     * 「2026년 9월 1일 (화)」였고 ◀▶ 는 하루씩 움직였다. 그래서 강사 현황처럼
     * 조회 단위가 달인 화면에서 원장님은 **매일 눌러야 달이 바뀌는 줄** 알았다 —
     * 실제로는 같은 달 안에서 아무리 눌러도 숫자가 하나도 안 바뀐다.
     *
     * 화면이 「달로 본다」고 말했으면 조작도 달이어야 한다.
     *
     * ⚠️ 다음 달로는 못 간다. 아직 오지 않은 달의 집계는 존재하지 않고,
     *    빈 화면을 보여 주면 원장님은 데이터가 사라졌다고 읽는다.
     *    (일 단위의 `isToday` 비활성과 같은 관례다)
     */
    const prevTarget = unit === 'month' ? cur.subtract(1, 'month').startOf('month') : cur.subtract(1, 'day');
    const nextTargetRaw = unit === 'month' ? cur.add(1, 'month').startOf('month') : cur.add(1, 'day');
    /* 이번 달로 돌아올 때는 「오늘」로 — 월초로 보내면 지난 날짜를 보는 것처럼 보인다 */
    const nextTarget =
        unit === 'month' && nextTargetRaw.isSame(today, 'month') ? today : nextTargetRaw;
    const canPrev = unit === 'month' ? !prevTarget.endOf('month').isBefore(oldest) : selectable(prevTarget);
    const canNext = unit === 'month' ? !cur.isSame(today, 'month') : !isToday;

    /**
     * 주간 칩은 "고른 날이 속한 주"를 보여준다.
     * 예전에는 오늘 기준 최근 7일로 고정돼 있어서, 지난달로 가면
     * 칩이 그대로라 화면이 안 바뀐 것처럼 보였다.
     */
    const week = useMemo(() => {
        const start = cur.startOf('week');
        return Array.from({ length: 7 }, (_, i) => start.add(i, 'day'));
    }, [cur]);

    /** 달력 격자 */
    const cells = useMemo(() => {
        const first = viewMonth.startOf('month');
        const pad = first.day();
        const days = viewMonth.daysInMonth();
        const out: (Dayjs | null)[] = [];
        for (let i = 0; i < pad; i += 1) out.push(null);
        for (let i = 0; i < days; i += 1) out.push(first.add(i, 'day'));
        while (out.length % 7 !== 0) out.push(null);
        return out;
    }, [viewMonth]);

    const canPrevMonth = viewMonth.subtract(1, 'month').endOf('month').isAfter(oldest);
    const canNextMonth = viewMonth.add(1, 'month').startOf('month').isBefore(today.add(1, 'day'));

    const arrowStyle = (enabled: boolean) => ({
        width: 34,
        height: 34,
        borderRadius: 9,
        border: `1px solid ${BORDER}`,
        background: '#FFFFFF',
        color: enabled ? '#425063' : '#D3DAE4',
        fontSize: 16,
        cursor: enabled ? ('pointer' as const) : ('not-allowed' as const),
        flex: 'none' as const,
    });

    return (
        <div
            ref={wrapRef}
            className="relative flex w-full items-center rounded-[12px] border border-solid bg-white"
            style={{ borderColor: BORDER, padding: '10px 14px', gap: 10 }}
        >
            <button
                type="button"
                onClick={() => canPrev && onChange(prevTarget)}
                disabled={!canPrev}
                aria-label={unit === 'month' ? '이전 달' : '이전 날짜'}
                style={arrowStyle(canPrev)}
            >
                ‹
            </button>

            {/* 날짜 — 누르면 달력 */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label="달력 열기"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                /*
                 * ═══════════════════════════════════════════════════════
                 * 🔴 **테두리를 Tailwind 클래스로 주지 마라** (2026-09-02 QA39)
                 * ═══════════════════════════════════════════════════════
                 * 직전 커밋에서 hover 를 살리려고 `border border-solid
                 * border-transparent` 로 옮겼다가 **검은 테두리**가 생겼다.
                 *
                 * 원인은 이 레포의 `tailwind.config.js` 다. `theme.colors` 를
                 * **통째로 교체**하면서 `transparent` 키를 넣지 않았다. 그래서
                 *   · `border-transparent` 라는 클래스가 아예 생성되지 않고
                 *   · `borderColor.DEFAULT` 도 `colors.gray.200` 이 없어
                 *     `currentColor` 로 떨어진다
                 * 결국 `border` 가 폭만 주고 색은 **글자색(진한 남색/검정)** 이 된다.
                 *
                 * ⚠️ 이 함정은 `border-transparent`·`border-gray-*` 같은
                 *    **테마 색 이름을 쓰는 모든 곳**에 있다. 임의값
                 *    (`border-[#E3EAF3]`)은 JIT 이라 항상 생성돼 안전하다.
                 *
                 * 그래서 색은 이 파일이 이미 쓰는 토큰으로 **인라인**에 되돌리고,
                 * hover 만 상태로 만든다. 새 색을 만들지 않는다 —
                 * 평소 `BORDER`(카드 테두리) · 열림/포커스 `#B9CBE6` ·
                 * 배경 `#F4F7FC` 전부 이 화면에 이미 있는 값이다.
                 */
                className="flex flex-col items-center rounded-[9px] outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#B9CBE6]"
                style={{
                    /*
                     * ═══════════════════════════════════════════════════════
                     * 🔴 **가장 긴 제목이 딱 들어갈 만큼만** (2026-09-02 QA8)
                     * ═══════════════════════════════════════════════════════
                     * 0 으로 두면 자릿수가 바뀔 때(「9월 9일」→「9월 10일」)
                     * 폭이 변해 화살표가 좌우로 튄다. 그래서 최장 문자열 기준으로
                     * 고정한다 — 어느 날짜에서도 `›` 의 x 좌표가 같다.
                     *
                     * 본문 폰트는 Noto Sans KR(`index.css`). 그 폰트의 자간은
                     * 숫자 0.6em · 한글 1.0em · 공백 0.26em · 괄호 0.35em 이고
                     * 여기 제목은 16.5px, letterSpacing -0.02em 이다.
                     *   일 단위 최장 「2026년 12월 22일 (수)」
                     *     숫자8×9.9 + 한글4×16.5 + 공백3×4.29 + 괄호2×5.78
                     *     − 자간 17×0.33 ≈ 164 · +패딩20 +테두리2 ≈ 186 → 203
                     *   달 단위 최장 「2026년 12월」 ≈ 122 → 150
                     *   (캐럿이 빠져 실제 글자폭은 17px 줄었지만, 폰트 폴백까지
                     *    감안해 여유를 남긴 값을 그대로 쓴다 — 좁히면 대칭이 깨진다)
                     *
                     * ⚠️ 계산값이라 폰트가 바뀌면 다시 재야 한다. 남는 폭은
                     *    가운데 정렬이 **양쪽으로 똑같이** 나눠 가지므로,
                     *    몇 px 어긋나도 좌우 대칭은 유지된다.
                     */
                    minWidth: unit === 'month' ? 150 : 203,
                    padding: '4px 10px',
                    /*
                     * 평소에는 **테두리가 보이지 않는다.** 눌리는 요소라는 것은
                     * hover 에서만 드러난다 — 날짜 줄은 카드 안의 제목이지
                     * 독립된 버튼처럼 보일 자리가 아니다.
                     */
                    border: `1px solid ${open ? '#B9CBE6' : hover ? BORDER : 'transparent'}`,
                    background: open || hover ? '#F4F7FC' : 'transparent',
                    transition: 'background-color 120ms ease, border-color 120ms ease',
                    cursor: 'pointer',
                    /*
                     * 🔴 가운데 정렬 — `‹ 제목 ›` 을 좌우 대칭으로 (QA8 정정 2).
                     *    캐럿이 오른쪽 끝에 있던 시절에는 왼쪽 정렬이 맞았지만,
                     *    캐럿을 없앤 지금은 남는 폭이 한쪽에만 쏠려 비대칭이 된다.
                     */
                    textAlign: 'center',
                }}
            >
                {/*
                  🔴 **캐럿(▾/▲)을 없앴다** (2026-09-02 QA8 정정 2, 대표 지시).

                  캐럿이 오른쪽에만 붙어 있어서 `‹ 제목 ›` 의 좌우 대칭이 깨졌다.
                  아이콘만 뺐고 **동작은 그대로다** — 제목 전체가 버튼이고,
                  누르면 달력이 열린다.

                  ⚠️ 「누를 수 있다」는 신호를 잃지 않게 위 버튼이
                     `aria-haspopup`·`aria-expanded` 와 hover/focus 배경을 갖는다.
                     아이콘을 지우면서 그것까지 지우면 그냥 글자가 된다.
                */}
                <span className="flex w-full items-center justify-center">
                    <span
                        style={{
                            fontSize: 16.5,
                            fontWeight: 800,
                            color: '#16202E',
                            letterSpacing: '-0.02em',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {/* 달로 조회하는 화면에 일자·요일을 적으면 그날 것으로 읽힌다 */}
                        {unit === 'month'
                            ? cur.format('YYYY년 M월')
                            : cur.format('YYYY년 M월 D일 (ddd)')}
                    </span>
                </span>
                <span
                    style={{
                        fontSize: 12.5,
                        color: isCurrentPeriod ? '#00795F' : '#B45309',
                        /* 제목과 붙어 보이던 것을 한 칸 띄운다 (2026-09-02 QA8) */
                        marginTop: 3,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {unit === 'month'
                        ? `${cur.format('M월')} ${label}`
                        : isToday
                          ? `오늘 ${label}`
                          : `${diff}일 전 ${label}`}
                </span>
            </button>

            <button
                type="button"
                onClick={() => canNext && onChange(nextTarget)}
                disabled={!canNext}
                aria-label={unit === 'month' ? '다음 달' : '다음 날짜'}
                style={arrowStyle(canNext)}
            >
                ›
            </button>

            {!isToday && (
                <button
                    type="button"
                    onClick={() => onChange(today)}
                    className="rounded-[8px]"
                    style={{
                        height: 32,
                        padding: '0 12px',
                        border: `1px solid ${BORDER}`,
                        background: '#FFFFFF',
                        color: '#2C4C8C',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                        flex: 'none',
                    }}
                >
                    오늘로
                </button>
            )}

            {/*
              이 주 — 고른 날짜를 따라 움직인다.

              🔴 **달 단위 화면에서는 그리지 않는다** (2026-09-01 QA28).
                 「일30 월31 오늘1 수2 …」 칩이 떠 있으면, 달로 집계하는 화면인데도
                 날짜를 고르는 화면으로 읽힌다. 실제로 칩을 눌러도 같은 달 안에서는
                 숫자가 하나도 안 바뀌어서, 원장님 눈에는 **먹통인 화면**이 된다.
                 (같은 이유로 제목의 일자·요일도 뺐다)

              🔴 **자리를 채우는 빈 칸도 두지 않는다** (2026-09-02 QA8).
                 칩을 뺀 자리에 `ml-auto` 짜리 빈 `div` 를 남겨 뒀었다. 그 자체로는
                 폭이 0 이라 화살표를 밀지는 않지만, 「오른쪽에 뭔가 있다」는 흔적만
                 남아 다음 사람이 이 줄을 `justify-between` 으로 오해하기 쉽다.
                 달 단위에서는 `‹ 제목 ›` 이 왼쪽에 한 덩어리로 붙고 오른쪽은
                 그냥 비워 둔다 — 남는 공간은 `flex-start` 가 알아서 뒤로 보낸다.
            */}
            {unit === 'month' ? null : (
            <div className="ml-auto flex items-center" style={{ gap: 5 }}>
                {week.map((d) => {
                    const active = d.isSame(cur, 'day');
                    const isTodayChip = d.isSame(today, 'day');
                    const ok = selectable(d);
                    return (
                        <button
                            key={d.format('YYYY-MM-DD')}
                            type="button"
                            onClick={() => go(d)}
                            disabled={!ok}
                            title={d.format('M월 D일 (ddd)')}
                            className="flex flex-col items-center justify-center rounded-[8px]"
                            style={{
                                width: 46,
                                height: 42,
                                border: `1px solid ${active ? '#1A305D' : isTodayChip ? '#B9CBE6' : '#E9EDF4'}`,
                                background: active ? '#1A305D' : '#FFFFFF',
                                cursor: ok ? 'pointer' : 'not-allowed',
                                opacity: ok ? 1 : 0.35,
                                padding: 0,
                            }}
                        >
                            <span style={{ fontSize: 10.5, color: active ? '#9DB4D8' : '#A7B0BC' }}>
                                {isTodayChip ? '오늘' : d.format('ddd')}
                            </span>
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: active ? '#FFFFFF' : '#425063',
                                    marginTop: 1,
                                }}
                            >
                                {d.date()}
                            </span>
                        </button>
                    );
                })}
            </div>
            )}

            {/* 달력 */}
            {open && (
                <div
                    className="absolute rounded-[13px]"
                    style={{
                        top: 'calc(100% + 8px)',
                        left: 52,
                        width: 302,
                        background: '#FFFFFF',
                        border: `1px solid ${BORDER}`,
                        boxShadow: '0 16px 40px rgba(16,32,64,0.16)',
                        padding: 14,
                        zIndex: 60,
                    }}
                >
                    <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
                        <button
                            type="button"
                            onClick={() => canPrevMonth && setViewMonth((m) => m.subtract(1, 'month'))}
                            disabled={!canPrevMonth}
                            aria-label="이전 달"
                            style={{ ...arrowStyle(canPrevMonth), width: 30, height: 30, fontSize: 14 }}
                        >
                            ‹
                        </button>
                        <span style={{ fontSize: 15.5, fontWeight: 800, color: '#16202E' }}>
                            {viewMonth.format('YYYY년 M월')}
                        </span>
                        <button
                            type="button"
                            onClick={() => canNextMonth && setViewMonth((m) => m.add(1, 'month'))}
                            disabled={!canNextMonth}
                            aria-label="다음 달"
                            style={{ ...arrowStyle(canNextMonth), width: 30, height: 30, fontSize: 14 }}
                        >
                            ›
                        </button>
                    </div>

                    <div className="grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
                        {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                            <span
                                key={d}
                                style={{
                                    fontSize: 11.5,
                                    color: i === 0 ? '#E06A72' : i === 6 ? '#5B8DE0' : '#93A0AE',
                                    textAlign: 'center',
                                    paddingBottom: 4,
                                }}
                            >
                                {d}
                            </span>
                        ))}
                        {cells.map((d, i) => {
                            if (!d) return <span key={`e${i}`} style={{ height: 34 }} />;
                            const ok = selectable(d);
                            const active = d.isSame(cur, 'day');
                            const isTodayCell = d.isSame(today, 'day');
                            return (
                                <button
                                    key={d.format('YYYY-MM-DD')}
                                    type="button"
                                    disabled={!ok}
                                    onClick={() => {
                                        go(d);
                                        setOpen(false);
                                    }}
                                    className="rounded-[7px]"
                                    style={{
                                        height: 34,
                                        border: isTodayCell && !active ? '1px solid #B9CBE6' : '1px solid transparent',
                                        background: active ? '#1A305D' : 'transparent',
                                        color: active ? '#FFFFFF' : ok ? '#3A4757' : '#D3DAE4',
                                        fontSize: 13.5,
                                        fontWeight: active || isTodayCell ? 700 : 500,
                                        cursor: ok ? 'pointer' : 'not-allowed',
                                        padding: 0,
                                    }}
                                >
                                    {d.date()}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="flex items-center justify-between"
                        style={{ marginTop: 12, paddingTop: 11, borderTop: `1px solid ${BORDER}` }}
                    >
                        <span style={{ fontSize: 12, color: '#93A0AE' }}>
                            최근 {MAX_PAST_DAYS}일까지 볼 수 있습니다
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                onChange(today);
                                setOpen(false);
                            }}
                            className="rounded-[8px]"
                            style={{
                                height: 32,
                                padding: '0 14px',
                                background: '#1A305D',
                                border: 'none',
                                color: '#FFFFFF',
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: 'pointer',
                            }}
                        >
                            오늘로
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * 과거를 보고 있을 때 붙는 안내.
 *
 * "지금은 못 합니다"가 아니라 "지금 해도 되는데 이건 알고 하세요"가 맞다.
 * 밀린 브리핑을 처리하는 건 정상 운영이고, 다만 숫자가 그날 기준이라는 것만 알면 된다.
 */
export const PastDateBanner = ({
    date,
    onBackToToday,
    unit = 'day',
}: {
    date: Dayjs;
    onBackToToday: () => void;
    /**
     * 이 화면의 집계 단위.
     *
     * 강사 현황은 날짜를 고르지만 조회는 **달 단위**입니다.
     * 그런데 이 배너가 늘 일 단위로 말해서, 같은 달 안에서 날짜만 바꿔도
     * "숫자는 3일 전 기준입니다"라고 했습니다 — 숫자는 하나도 안 바뀌는데
     * 바뀐 척을 하니 원장님 눈에는 날짜가 먹통인 화면이 됩니다.
     * 단위를 밝혀서 화면이 사실만 말하게 합니다.
     */
    unit?: 'day' | 'month';
}) => {
    const diff = dayjs().startOf('day').diff(date.startOf('day'), 'day');
    if (unit === 'month') {
        return (
            <div
                className="flex w-full items-center rounded-[12px]"
                style={{ background: '#EDF2FA', border: '1px solid #C9D8EE', padding: '13px 18px', gap: 10 }}
            >
                <span style={{ fontSize: 16 }}>🗓</span>
                <span style={{ fontSize: 14.5, color: '#20406F', fontWeight: 700, flex: 'none' }}>
                    {date.format('YYYY년 M월')} 집계입니다
                </span>
                <span style={{ fontSize: 13.5, color: '#5B7BA8' }}>
                    이 화면은 달 단위로 집계합니다. 같은 달 안에서는 어느 날을 고르셔도 숫자가 같습니다.
                </span>
                <button
                    type="button"
                    onClick={onBackToToday}
                    className="ml-auto rounded-[8px]"
                    style={{
                        height: 34,
                        padding: '0 14px',
                        background: '#FFFFFF',
                        border: '1px solid #B9CBE6',
                        color: '#20406F',
                        fontSize: 13.5,
                        fontWeight: 700,
                        cursor: 'pointer',
                        flex: 'none',
                        whiteSpace: 'nowrap',
                    }}
                >
                    이번 달로 돌아가기
                </button>
            </div>
        );
    }
    return (
        <div
            className="flex w-full items-center rounded-[12px]"
            style={{ background: '#EDF2FA', border: '1px solid #C9D8EE', padding: '13px 18px', gap: 10 }}
        >
            <span style={{ fontSize: 16 }}>🕐</span>
            <span style={{ fontSize: 14.5, color: '#20406F', fontWeight: 700, flex: 'none' }}>
                {date.format('M월 D일')} 기록입니다
            </span>
            <span style={{ fontSize: 13.5, color: '#5B7BA8' }}>
                밀린 건도 지금 바로 지시하실 수 있습니다. 다만 숫자는 {diff}일 전 기준이라, 그 사이 회원이 수업을
                받았을 수 있습니다.
            </span>
            <button
                type="button"
                onClick={onBackToToday}
                className="ml-auto rounded-[8px]"
                style={{
                    height: 34,
                    padding: '0 14px',
                    background: '#FFFFFF',
                    border: '1px solid #B9CBE6',
                    color: '#20406F',
                    fontSize: 13.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    flex: 'none',
                    whiteSpace: 'nowrap',
                }}
            >
                오늘로 돌아가기
            </button>
        </div>
    );
};

export default DateNavigator;
